import type { ReactNode } from "react";
import { createBrowserRouter } from "react-router";
import type { RouteObject } from "react-router";

import { ActivityPage } from "../features/activity/ActivityPage";
import { InviteCodesPage } from "../features/admin/InviteCodesPage";
import { RequireModerator } from "../features/admin/RequireModerator";
import { LoginPage } from "../features/auth/LoginPage";
import { MapPage } from "../features/map/MapPage";
import { PrivacyPage } from "../features/privacy/PrivacyPage";
import { ModerationPage } from "../features/moderation/ModerationPage";
import { CompaniesPage } from "../features/companies/CompaniesPage";
import { OnboardingPage } from "../features/profile/OnboardingPage";
import { ProfilePage } from "../features/profile/ProfilePage";
import { ReportPage } from "../features/report/ReportPage";
import { SearchPage } from "../features/search/SearchPage";
import { StatsPage } from "../features/stats/StatsPage";
import { RecoverPage } from "../features/auth/RecoverPage";
import { RegisterPage } from "../features/auth/RegisterPage";
import { RequireAuth } from "../features/auth/RequireAuth";
import { ComponentsPage } from "../features/dev/ComponentsPage";
import { LaunchPage } from "../features/launch/LaunchPage";
import { nl } from "../i18n/nl";
import { AppShell } from "../layout/AppShell";
import { NotFound } from "./NotFound";
import { ScreenPlaceholder } from "./ScreenPlaceholder";
import { LAUNCH_PATH, PRIVACY_PATH, PUBLIC_PATHS, shellScreens, standaloneScreens } from "./screens";
import type { ScreenHandle } from "./screens";

const ADMIN_PREFIX = "/beheer/";

/** Built screens; the others still show a placeholder. */
const screenPages: Partial<Record<(typeof shellScreens)[number]["path"], ReactNode>> = {
  "/": <CompaniesPage />,
  "/profiel": <ProfilePage />,
  "/melden": <ReportPage />,
  "/activiteit": <ActivityPage />,
  "/kaart": <MapPage />,
  "/zoeken": <SearchPage />,
  "/beheer/moderatie": <ModerationPage />,
  "/beheer/statistieken": <StatsPage />,
  "/beheer/klascodes": <InviteCodesPage />,
};

export const routes: RouteObject[] = [
  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppShell />,
        children: [
          ...shellScreens.map(({ path, title }): RouteObject => {
            const page = screenPages[path] ?? <ScreenPlaceholder title={title} />;
            return {
              path,
              handle: { title } satisfies ScreenHandle,
              element: path.startsWith(ADMIN_PREFIX) ? <RequireModerator>{page}</RequireModerator> : page,
            };
          }),
          {
            // Deep link to one company (also used by the notification mails).
            path: "/bedrijven/:companyId",
            handle: { title: nl.screens.companies } satisfies ScreenHandle,
            element: <CompaniesPage />,
          },
          // Dev-only component overview; tree-shaken out of production builds.
          ...(import.meta.env.DEV
            ? [
                {
                  path: "/dev/componenten",
                  handle: { title: nl.dev.componentsTitle } satisfies ScreenHandle,
                  element: <ComponentsPage />,
                } satisfies RouteObject,
              ]
            : []),
          { path: "*", element: <NotFound /> },
        ],
      },
      ...standaloneScreens.map(
        ({ path, title }): RouteObject => ({
          path,
          handle: { title } satisfies ScreenHandle,
          // `/welkom` is the only standalone screen (onboarding).
          element: <OnboardingPage />,
        }),
      ),
    ],
  },
  { path: LAUNCH_PATH, handle: { title: nl.screens.launch } satisfies ScreenHandle, element: <LaunchPage /> },
  { path: PRIVACY_PATH, handle: { title: nl.screens.privacy } satisfies ScreenHandle, element: <PrivacyPage /> },
  { path: PUBLIC_PATHS.login, handle: { title: nl.screens.login } satisfies ScreenHandle, element: <LoginPage /> },
  { path: PUBLIC_PATHS.register, handle: { title: nl.screens.register } satisfies ScreenHandle, element: <RegisterPage /> },
  { path: PUBLIC_PATHS.recover, handle: { title: nl.screens.recover } satisfies ScreenHandle, element: <RecoverPage /> },
];

export function createRouter() {
  return createBrowserRouter(routes);
}
